import { logger } from './utils.js';

class MarketingServicesManager {
  constructor() {
    this.services = new Map();
    this.isInitialized = false;
  }

  // Registra un servizio di marketing
  registerService(serviceId, config) {
    this.services.set(serviceId, {
      ...config,
      isLoaded: false
    });
    logger.log(`Marketing service registered: ${serviceId}`);
  }

  // Carica un servizio specifico
  async loadService(serviceId) {
    const service = this.services.get(serviceId);
    if (!service) {
      logger.warn(`Service not found: ${serviceId}`);
      return;
    }

    if (service.isLoaded) {
      logger.log(`Service already loaded: ${serviceId}`);
      return;
    }

    try {
      logger.log(`Loading marketing service: ${serviceId}`);
      
      // Carica lo script
      await this._loadScript(service.scriptUrl, serviceId);
      
      // Inizializza il servizio se c'è una funzione di inizializzazione
      if (service.initialize) {
        await service.initialize();
      }
      
      service.isLoaded = true;
      logger.log(`Marketing service loaded successfully: ${serviceId}`);
    } catch (error) {
      logger.error(`Failed to load marketing service: ${serviceId}`, error);
    }
  }

  // Carica tutti i servizi di marketing
  async loadAllServices() {
    logger.log('Loading all marketing services...');
    
    const loadPromises = Array.from(this.services.keys()).map(serviceId => 
      this.loadService(serviceId)
    );
    
    await Promise.allSettled(loadPromises);
    this.isInitialized = true;
    logger.log('All marketing services loaded');
  }

  // Rimuove un servizio specifico
  removeService(serviceId) {
    const service = this.services.get(serviceId);
    if (!service) return;

    try {
      // Rimuovi lo script
      this._removeScript(serviceId);
      
      // Cleanup se c'è una funzione di cleanup
      if (service.cleanup) {
        service.cleanup();
      }
      
      service.isLoaded = false;
      logger.log(`Marketing service removed: ${serviceId}`);
    } catch (error) {
      logger.error(`Failed to remove marketing service: ${serviceId}`, error);
    }
  }

  // Rimuove tutti i servizi
  removeAllServices() {
    logger.log('Removing all marketing services...');
    
    Array.from(this.services.keys()).forEach(serviceId => {
      this.removeService(serviceId);
    });
    
    this.isInitialized = false;
    logger.log('All marketing services removed');
  }

  // Aggiorna il consenso per tutti i servizi
  updateConsent(consentSettings) {
    logger.log('Updating marketing services consent', consentSettings);
    
    this.services.forEach((service, serviceId) => {
      if (service.updateConsent) {
        try {
          service.updateConsent(consentSettings);
        } catch (error) {
          logger.error(`Failed to update consent for service: ${serviceId}`, error);
        }
      }
    });
  }

  // Carica script
  _loadScript(src, id) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.id = id;
      script.async = true;
      
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      
      document.head.appendChild(script);
    });
  }

  // Rimuovi script
  _removeScript(id) {
    const script = document.getElementById(id);
    if (script) {
      script.remove();
    }
  }

  // Controllo stato
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      services: Array.from(this.services.entries()).map(([id, service]) => ({
        id,
        isLoaded: service.isLoaded,
        config: service
      }))
    };
  }
}

// Singleton instance
const marketingServicesManager = new MarketingServicesManager();

export default marketingServicesManager; 